import {
  HardwareConflictValues,
  HardwareConflictsContext,
  ConflictHint,
  useHardwareConflict,
} from '../components/HardwareConflictField.js';
import { fetchJobHardwareContext } from '../api.js';
import { HardwareConflictsPanel } from '../components/HardwareConflictsPanel.js';
import { resolveHardwareConflict } from '../api.js';
import { MissingPhotoOverrideDialog } from '../components/MissingPhotoOverrideDialog.js';
import { ReportSidebarLayout } from '../components/ReportSidebar.js';
import { defaultDcIsolator } from '../lib/hardware-rows.js';
import { HardwarePhotoCheck } from '../components/HardwarePhotoCheck.js';
import { reindexHardwareCheckSelections } from '../lib/hardware-rows.js';
import {
  acceptOnlyReviewsKey,
  hardwareCheckSelectionCurrent,
  mergeHardwareCheckSelections,
  type AcceptOnlyReview,
} from '../lib/auto-hardware-checks.js';
import { type HardwareCheckSelection } from '@cer-autofill/shared';
import { HardwareLabelSuggestions } from '../components/HardwareLabelSuggestions.js';
/**
 * Full review surface for one report revision.
 *
 * Renders the submission JSON returned by GET /api/admin/submission/:jobId/:submittedAt
 * across the eight cards the tech head needs to review a job:
 *   - Action bar (status + Approve / Send back)
 *   - Cover sheet
 *   - Consent (with rendered signatures)
 *   - Hardware
 *   - Checklist (grouped by section)
 *   - Photos (grouped by promptKey)
 *   - Audit trail
 *
 * Photos render via PhotoThumb, which fetches blobs through authedFetch (the
 * native `<img>` can't send our X-Field-Device-Token header).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  generalRecommendationWording,
  calculateArrayTotals,
  PV_NON_CHECKLIST_ITEMS,
  ADMIN_TRANSITIONS,
  assessmentLabelForValue,
  assessmentValueForClauseResponse,
  findAssessmentOption,
  isCustomResponse,
  withRoofCavity,
  ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS,
  type RoofCavityNotInspectedReason,
  estimateInspectionWindow,
  formatInstantForCover,
  parseTimestampToInstant,
  resolveInspectionOffsetMinutes,
  countOutstandingNow,
  fieldReadiness,
  promptKeysOnReport,
  holdUnsavedWork,
  findPromptByKey,
  getPromptsForJob,
  GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY,
  UNASSIGNED_PROMPT_KEY,
  type AdminStatusTransition,
  type AdminTransitionTarget,
  type ChecklistSection,
  type ClauseResponse,
  type FieldJobInspectionType,
  type FieldHardware,
  type FieldReport,
  type PhotoQuarterTurn,
  type StoredPhotoManifest,
  type QaReport,
  type MissingClausePhoto,
  type StoredReport,
  seedBtyBatteryRow,
  labellingConfirmationRequired,
  withAlternativeSupplyDefault,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  approveForUpload,
  deleteReportPhoto,
  qaReportFromApiError,
  missingPhotoOverrideFromApiError,
  labellingUnconfirmedFromApiError,
  supersededByFromApiError,
  editReport,
  fetchCase,
  fetchPhotos,
  fetchPhotoBlobUrl,
  fetchReport,
  rekeyReportPhoto,
  rotateReportPhoto,
  openSignedConsentPdf,
  transitionStatus,
  uploadQaPhoto,
} from '../api.js';
import {
  EDIT_HARDWARE_ANCHOR_ID,
  focusSwitchboardLabellingSelect,
  LABELLING_CHIP_LABEL,
  LABELLING_UNCONFIRMED_BANNER,
  QA_OVERRIDE_LABELLING_NOTE,
} from '../lib/labelling-approve-ux.js';
import { formatDateTime } from '../format.js';
import { ReadinessPill, StatusPill } from '../components/StatusPill.js';
import { EditablePhotoTile } from '../components/EditablePhotoTile.js';
import { ReportPhotoEditor } from '../components/ReportPhotoEditor.js';
import { PhotoThumb } from '../components/PhotoThumb.js';
import { openFileInNewTab } from '../lib/open-file.js';
import { PhotoLightbox, type LightboxPhoto } from '../components/PhotoLightbox.js';
import { QaPanel } from '../components/QaPanel.js';
import { DropboxPanel } from '../components/DropboxPanel.js';
import {
  FilingPicker,
  filingTargetsFor,
  type FilingTargetGroup,
} from '../components/FilingPicker.js';
import {
  applyPhotoRefile,
  reportChecklistRows,
  isRoutineChecklistResponse,
} from '../lib/report-checklist.js';
import { ReportChecklistEditor } from '../components/ReportChecklistEditor.js';
import { ReportHardwareEditor } from '../components/ReportHardwareEditor.js';
import { getSession } from '../session.js';
import { clearDraft, loadDraft, saveDraft, type StoredDraft } from '../lib/report-draft.js';
import { AuditTimeline } from '../components/AuditTimeline.js';

function inspectionKindForJob(jobId: string): FieldJobInspectionType {
  const upper = jobId.toUpperCase();
  if (upper.startsWith('BTY')) return 'battery';
  if (upper.startsWith('PVD')) return 'pv';
  return 'combo';
}

const SECTION_LABEL: Record<ChecklistSection, string> = {
  'cer-pv': 'CER PV',
  'battery-section-4-ac': 'Battery — Section 4 (AC coupled)',
  'battery-section-5-dc': 'Battery — Section 5 (DC coupled)',
};

/** Stable per-clause key shared by the read view and the editor's focus. */
function clauseAnchorKey(clause: Pick<ClauseResponse, 'section' | 'clauseId'>): string {
  return `${clause.section}:${clause.clauseId}`;
}

function photoPromptLabel(promptKey: string): string {
  if (promptKey.startsWith('clause:')) return promptKey.slice('clause:'.length);
  const prompt = findPromptByKey(promptKey);
  if (prompt) return `${prompt.subject}: ${prompt.shot}`;
  return promptKey
    .split('-')
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
}

/** Materialise the rows displayed by the editor so photo checks can target blank equipment. */
function initialiseHardwareDraft(draft: FieldReport): FieldReport {
  const hardware = withAlternativeSupplyDefault(draft.jobId, { ...draft.hardware });
  if (draft.jobId.startsWith('PVD')) {
    hardware.inverters = (hardware.inverters.length ? hardware.inverters : [{}]).map((row) => ({
      ...row,
      galvanicallyIsolated: row.galvanicallyIsolated ?? false,
    }));
    hardware.modules = hardware.modules.length ? hardware.modules : [{}];
    hardware.acIsolators = hardware.acIsolators.length ? hardware.acIsolators : [{}];
    hardware.dcIsolators = (hardware.dcIsolators.length ? hardware.dcIsolators : [{}]).map((row) =>
      defaultDcIsolator(row),
    );
  } else if (draft.jobId.startsWith('BTY')) {
    hardware.batteries = seedBtyBatteryRow(draft.jobId, hardware).batteries;
  } else {
    hardware.batteries = hardware.batteries.length ? hardware.batteries : [{}];
  }
  return { ...draft, hardware: calculateArrayTotals(hardware) };
}

/** Fill only blank report hardware from the client/CER intake record. */
function mergeHardwarePrefill(current: FieldHardware, source: FieldHardware): FieldHardware {
  const mergeRows = <T extends object>(existing: readonly T[], incoming: readonly T[]): T[] => {
    if (existing.length === 0) return [...incoming];
    return existing.map((row, index) => {
      const supplied = incoming[index];
      if (!supplied) return row;
      const merged = { ...row } as Record<string, unknown>;
      for (const [key, value] of Object.entries(supplied)) {
        const old = merged[key];
        if (
          (old === undefined || old === '' || (Array.isArray(old) && old.length === 0)) &&
          value !== undefined
        ) {
          merged[key] = value;
        }
      }
      return merged as T;
    });
  };
  return calculateArrayTotals({
    ...current,
    inverters: mergeRows(current.inverters, source.inverters),
    modules: mergeRows(current.modules, source.modules),
    batteries: mergeRows(current.batteries, source.batteries),
    acIsolators: mergeRows(current.acIsolators, source.acIsolators),
    dcIsolators: mergeRows(current.dcIsolators, source.dcIsolators),
    totalArrayVoltageOpenCircuitV:
      current.totalArrayVoltageOpenCircuitV ?? source.totalArrayVoltageOpenCircuitV,
    totalArrayCurrentShortCircuitA:
      current.totalArrayCurrentShortCircuitA ?? source.totalArrayCurrentShortCircuitA,
  });
}

export function ReportDetailScreen() {
  const params = useParams<{ jobId: string; submittedAt: string }>();
  const jobId = params.jobId ?? '';
  const submittedAt = params.submittedAt ?? '';
  const navigate = useNavigate();

  const [hardwareContextStatus, setHardwareContextStatus] = useState<
    'loading' | 'failed' | 'loaded'
  >('loading');
  const [latestHardware, setLatestHardware] = useState<Awaited<
    ReturnType<typeof fetchJobHardwareContext>
  > | null>(null);
  const [data, setData] = useState<StoredReport | null>(null);
  const [photos, setPhotos] = useState<StoredPhotoManifest[]>([]);
  const [qaPhotoPrompt, setQaPhotoPrompt] = useState('');
  const [qaPhotoBusy, setQaPhotoBusy] = useState(false);
  const [qaPhotoError, setQaPhotoError] = useState<string | null>(null);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxPhoto | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editFocusKey, setEditFocusKey] = useState<string | null>(null);
  /** True when the editor pre-filled a blank inspection time from the photos. */
  const [windowSuggested, setWindowSuggested] = useState(false);
  const [photoChecks, setPhotoChecks] = useState<HardwareCheckSelection[]>([]);
  const [acceptOnlyReviews, setAcceptOnlyReviews] = useState<AcceptOnlyReview[]>([]);
  const [dismissedPhotoReviewIds, setDismissedPhotoReviewIds] = useState<string[]>([]);
  /** A draft found in localStorage on load, offered for restore. */
  const [recoverable, setRecoverable] = useState<StoredDraft | null>(null);
  const [editDraft, setEditDraft] = useState<FieldReport | null>(null);
  const onAcceptOnlyReviews = useCallback((reviews: readonly AcceptOnlyReview[]) => {
    setAcceptOnlyReviews((current) =>
      acceptOnlyReviewsKey(current) === acceptOnlyReviewsKey(reviews) ? current : [...reviews],
    );
  }, []);
  const onSeedEmptyBattery = useCallback(() => {
    setEditDraft((current) => {
      if (!current) return current;
      const hardware = seedBtyBatteryRow(current.jobId, current.hardware);
      return hardware === current.hardware ? current : { ...current, hardware };
    });
  }, []);
  const [editNote, setEditNote] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editInfo, setEditInfo] = useState<string | null>(null);
  const [photoActionBusyId, setPhotoActionBusyId] = useState<string | null>(null);
  const [photoActionError, setPhotoActionError] = useState<string | null>(null);
  const [refilingPhotoId, setRefilingPhotoId] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalApproved, setPortalApproved] = useState(false);
  /** The QA report a 409 came back with, when Approve for Portal was refused. */
  const [portalBlocked, setPortalBlocked] = useState<QaReport | null>(null);
  /** CER-77: approve refused because switchboard labelling is still unconfirmed. */
  const [portalLabellingBlocked, setPortalLabellingBlocked] = useState(false);
  /** CER-58: missing photos need a checkbox and a reason, not a bare force. */
  const [missingPhotoGate, setMissingPhotoGate] = useState<{
    report: QaReport;
    items: MissingClausePhoto[];
  } | null>(null);
  /** Newest revision's submittedAt, when approve refused this one as superseded. */
  const [portalSuperseded, setPortalSuperseded] = useState<string | null>(null);
  /**
   * Sticky once accepted. The two refusals can fire in sequence — an older
   * revision that also has QA findings — and without this the QA "approve
   * anyway" click would drop the supersession consent and bounce off the first
   * check again, forever.
   */
  const [supersededAccepted, setSupersededAccepted] = useState(false);
  const [batteryInstallationType, setBatteryInstallationType] = useState<string | null>(null);
  const load = useCallback(() => {
    setLoadError(null);
    setLatestHardware(null);
    setHardwareContextStatus('loading');
    void fetchJobHardwareContext(jobId)
      .then((context) => {
        setLatestHardware(context);
        setHardwareContextStatus('loaded');
      })
      .catch(() => setHardwareContextStatus('failed'));
    void Promise.all([fetchReport(jobId, submittedAt), fetchPhotos(jobId)])
      .then(([sub, photoList]) => {
        setData(sub);
        setPhotos(photoList);
      })
      .catch((e: unknown) => {
        setLoadError(e instanceof Error ? e.message : 'Failed to load report.');
      });
  }, [jobId, submittedAt]);

  useEffect(() => {
    load();
  }, [load]);

  // Offer back an edit session that ended without saving. Checked once per
  // revision rather than on every render: the banner should appear when the
  // reviewer returns, not reappear after they dismiss it.
  useEffect(() => {
    setRecoverable(loadDraft(jobId, submittedAt));
    setPhotoChecks([]);
    setDismissedPhotoReviewIds([]);
    setEditDraft(null);
    setEditOpen(false);
  }, [jobId, submittedAt]);

  /*
   * Mirror the in-progress draft to localStorage.
   *
   * Debounced because the checklist editor fires per keystroke and
   * `JSON.stringify` over a full submission is not free. 800ms is long
   * enough to coalesce typing and short enough that a closed tab loses at
   * most a sentence.
   */
  useEffect(() => {
    if (!editOpen || !editDraft) return;
    const timer = setTimeout(
      () =>
        saveDraft(jobId, submittedAt, {
          submission: editDraft,
          note: editNote,
          hardwareChecks: photoChecks,
          dismissedPhotoReviewIds,
        }),
      800,
    );
    return () => clearTimeout(timer);
  }, [editOpen, editDraft, editNote, photoChecks, dismissedPhotoReviewIds, jobId, submittedAt]);

  // A new deploy must not reload this tab out from under an open Edit.
  // The draft is in memory (and only mirrored to localStorage after a pause).
  useEffect(() => {
    if (!editOpen) return;
    return holdUnsavedWork();
  }, [editOpen]);

  const photoById = useMemo(() => {
    const map = new Map<string, StoredPhotoManifest>();
    for (const p of photos) map.set(p.photoId, p);
    return map;
  }, [photos]);

  const photosByPromptKey = useMemo(() => {
    const groups = new Map<string, StoredPhotoManifest[]>();
    for (const p of photos) {
      if (p.promptKey === 'consent-signed-form') continue;
      const arr = groups.get(p.promptKey) ?? [];
      arr.push(p);
      groups.set(p.promptKey, arr);
    }
    // Stable display order: alphabetical by promptKey.
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [photos]);

  const clausesBySection = useMemo(
    () => (data ? [...reportChecklistRows(data.submission).entries()] : []),
    [data],
  );
  const [showRoutine, setShowRoutine] = useState(false);
  const unresolvedConflicts = data?.hardwareConflicts?.filter((c) => !c.resolution) ?? [];
  const isLatestRevision =
    hardwareContextStatus === 'loaded' && latestHardware?.submittedAt === data?.submittedAt;
  const approvalConflictCount = Math.max(
    latestHardware?.unresolvedConflictCount ?? 0,
    unresolvedConflicts.length,
  );
  const firstSubmissionMerge = data?.audit?.some((a) =>
    a.note?.startsWith('Field submission merged'),
  );
  const hasUnsavedEdits = editDraft !== null || recoverable !== null;
  const labellingUnsetOnBty =
    !!data && labellingConfirmationRequired(jobId, data.submission.hardware);

  /**
   * Approve for Portal — build the payload the browser extension reads.
   *
   * Two-step by design rather than two buttons: the first click asks the
   * backend without `force`, and an unclean QA run comes back 409 with the
   * report attached. We show what is wrong and offer to proceed. That way the
   * common path is one click, and overriding QA is a thing someone chose to do
   * after seeing what they were overriding.
   */
  async function doApproveForPortal(
    opts: {
      force?: boolean;
      approveSuperseded?: boolean;
      missingPhotoOverride?: { acknowledged: boolean; reason: string };
    } = {},
  ) {
    if (!data) return;
    if (approvalConflictCount) {
      setActionError(
        `Resolve ${approvalConflictCount} hardware conflict(s) before approving for portal.`,
      );
      return;
    }
    if (hasUnsavedEdits) {
      setActionError('Save your edits as a new revision before approving for the portal.');
      return;
    }
    setPortalBusy(true);
    setActionError(null);
    setPortalApproved(false);
    try {
      await approveForUpload(jobId, submittedAt, {
        ...opts,
        approveSuperseded: opts.approveSuperseded ?? supersededAccepted,
      });
      setPortalBlocked(null);
      setPortalLabellingBlocked(false);
      setMissingPhotoGate(null);
      setPortalSuperseded(null);
      setSupersededAccepted(false);
      setPortalApproved(true);
      load();
    } catch (e) {
      // Supersession is checked first on the server: publishing the wrong
      // revision is the more consequential refusal. Missing photos are next,
      // and they need their own acknowledgement — `force` does not cover them.
      const superseded = supersededByFromApiError(e);
      const missingPhotos = missingPhotoOverrideFromApiError(e);
      const labellingBlocked = labellingUnconfirmedFromApiError(e);
      const report = qaReportFromApiError(e);
      if (superseded) {
        setPortalSuperseded(superseded);
        setMissingPhotoGate(null);
        setPortalLabellingBlocked(false);
      } else if (missingPhotos) {
        setMissingPhotoGate(missingPhotos);
        setPortalBlocked(null);
        setPortalLabellingBlocked(false);
      } else if (labellingBlocked) {
        // CER-69 gate — force cannot bypass. Show a jump, not a bare red error.
        setPortalLabellingBlocked(true);
        setPortalBlocked(null);
        setMissingPhotoGate(null);
        setActionError(null);
      } else if (report) {
        setPortalBlocked(report);
        setMissingPhotoGate(null);
        setPortalLabellingBlocked(false);
      } else setActionError(apiErrorReason(e));
    } finally {
      setPortalBusy(false);
    }
  }

  async function doTransition(toStatus: AdminTransitionTarget, note?: string) {
    if (!data) return;
    setBusy(true);
    setActionError(null);
    try {
      const body: AdminStatusTransition = note ? { toStatus, note } : { toStatus };
      await transitionStatus(jobId, submittedAt, body);
      setSendBackOpen(false);
      load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Status transition failed.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The window the photos imply, for the cover-sheet editor.
   *
   * Derived from the stored photo manifests rather than the draft, because
   * the manifests carry the capture times the store actually holds. Recomputed
   * per render — it is arithmetic over a few dozen timestamps.
   */
  /**
   * Local time at the SITE — not at the Lambda (UTC) and not in whatever
   * timezone the reviewer's browser sits in. A legacy import has no device
   * offset and UTC photo timestamps, so without this a midday QLD inspection
   * reads as 02:19: UTC wearing local time's clothes.
   */
  const inspectionOffset = resolveInspectionOffsetMinutes({
    ...(data?.submission.deviceUtcOffsetMinutes !== undefined
      ? { deviceUtcOffsetMinutes: data.submission.deviceUtcOffsetMinutes }
      : {}),
    ...(data?.submission.cover.siteAddress !== undefined
      ? { siteAddress: data.submission.cover.siteAddress }
      : {}),
    atMs: Date.parse(data?.submission.cover.inspectionDate ?? submittedAt) || Date.now(),
  });

  const photoWindow = estimateInspectionWindow({
    capturedAts: photos
      .filter((p) => !p.promptKey.includes('consent') && p.captureTimeSource !== 'upload')
      .map((p) => p.capturedAt),
    deviceUtcOffsetMinutes: inspectionOffset,
  });

  /**
   * Render a cover timestamp for a `datetime-local` input.
   *
   * The input is naive by definition, so a zoned stored value must be
   * CONVERTED to site-local — slicing it would present UTC as local and put
   * a ten-hour error in front of the reviewer as though it were data. Naive
   * values round-trip unchanged.
   */
  const coverTimeForInput = (value: string | undefined): string => {
    if (!value) return '';
    const ms = parseTimestampToInstant(value, inspectionOffset);
    if (ms === undefined) return '';
    return formatInstantForCover(ms, inspectionOffset).slice(0, 16);
  };

  function openEditor(focusKey?: string) {
    if (!data) return;
    const draft = initialiseHardwareDraft(
      structuredClone(editDraft ?? recoverable?.submission ?? data.submission),
    );

    /*
     * Pre-fill a MISSING inspection time from the photos.
     *
     * A blank window is the common case behind the photo-timestamps finding,
     * and the photos already answer it — so the editor opens with the derived
     * value in the field rather than an empty box and a rule telling the
     * reviewer to work it out. It is a suggestion, not a silent write: the
     * fields are marked as derived, the value is visible before saving, and
     * nothing persists until Save. A time the inspector DID record is never
     * overwritten.
     */
    const suggested: Array<'started' | 'ended'> = [];
    // An insufficient estimate (one photo, or all photos at one instant) is
    // an incomplete photo dump, not a fast inspection — never pre-fill from it.
    if (photoWindow?.sufficient) {
      if (!draft.cover.inspectionStartedAt?.trim()) {
        draft.cover.inspectionStartedAt = photoWindow.startedAt;
        suggested.push('started');
      }
      if (!draft.cover.inspectionEndedAt?.trim()) {
        draft.cover.inspectionEndedAt = photoWindow.endedAt;
        suggested.push('ended');
      }
    }

    setAcceptOnlyReviews([]);
    setEditDraft(draft);
    void fetchCase(jobId)
      .then((inspectionJob) => {
        setBatteryInstallationType(inspectionJob.batteryInstallationType ?? null);
        if (!inspectionJob.hardware) return;
        setEditDraft((current) =>
          current
            ? {
                ...current,
                hardware: mergeHardwarePrefill(current.hardware, inspectionJob.hardware!),
              }
            : current,
        );
      })
      .catch(() => undefined);
    setEditNote(editDraft ? editNote : (recoverable?.note ?? ''));
    if (!editDraft) {
      setPhotoChecks(recoverable?.hardwareChecks ?? []);
      setDismissedPhotoReviewIds(recoverable?.dismissedPhotoReviewIds ?? []);
    }
    setEditError(null);
    setWindowSuggested(suggested.length > 0);
    setEditInfo(
      suggested.length > 0
        ? `Inspection ${suggested.length === 2 ? 'times were' : `${suggested[0]} time was`} blank — filled from ${photoWindow?.photoCount} photo timestamps. Check before saving.`
        : null,
    );
    setEditFocusKey(focusKey ?? null);
    setEditOpen(true);
  }

  /** Reopen the editor on a draft recovered from a previous session. */
  function restoreDraft(): void {
    if (!recoverable) return;
    setEditDraft(initialiseHardwareDraft(structuredClone(recoverable.submission)));
    setPhotoChecks(recoverable.hardwareChecks ?? []);
    setDismissedPhotoReviewIds(recoverable.dismissedPhotoReviewIds ?? []);
    setEditNote(recoverable.note);
    setEditError(null);
    setEditInfo(`Restored your unsaved edits from ${formatDateTime(recoverable.savedAt)}.`);
    setWindowSuggested(false);
    setEditFocusKey(null);
    setRecoverable(null);
    setEditOpen(true);
  }

  /** Throw away the in-progress edit — both in memory and in storage. */
  function discardDraft(): void {
    if (!window.confirm('Discard these edits? The saved report is not affected.')) return;
    clearDraft(jobId, submittedAt);
    setRecoverable(null);
    setEditDraft(null);
    setDismissedPhotoReviewIds([]);
    setEditNote('');
    setEditInfo(null);
    setEditOpen(false);
  }

  /** Scroll a section into view with a brief highlight, QA-row "Show me". */
  function jumpTo(anchorId: string) {
    const el = document.getElementById(anchorId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.remove('jump-flash');
    // Reflow so re-adding the class restarts the animation on repeat jumps.
    void el.offsetWidth;
    el.classList.add('jump-flash');
  }

  /** CER-77: open Edit → Hardware → focus Switchboard labelling select. */
  function showLabellingSelect() {
    if (!canEdit) {
      jumpTo(editOpen ? EDIT_HARDWARE_ANCHOR_ID : 'report-hardware');
      return;
    }
    openEditor();
    requestAnimationFrame(() => {
      jumpTo(EDIT_HARDWARE_ANCHOR_ID);
      // Second frame: the select mounts with the edit panel.
      requestAnimationFrame(() => {
        focusSwitchboardLabellingSelect();
      });
    });
  }

  /**
   * Where each QA rule's evidence lives on this page. The assessment rules
   * do better than a section jump: they open the editor focused on the first
   * clause the rule is complaining about, with the fix control in reach.
   */
  function showRule(ruleId: string) {
    if (!data) return;
    if (ruleId === 'nonconformance-assessments' || ruleId === 'assessment-alignment') {
      const offender = data.submission.checklist.find(
        (clause) =>
          clause.outcome === 'Non-Compliant' &&
          (!assessmentValueForClauseResponse(clause) || !clause.recommendation?.trim()),
      );
      if (offender && canEdit) {
        openEditor(clauseAnchorKey(offender));
        return;
      }
      jumpTo('report-checklist');
      return;
    }
    const target: Record<string, string> = {
      'coversheet-complete': editOpen ? 'edit-cover' : 'report-cover',
      'hardware-details': editOpen ? 'edit-hardware' : 'report-hardware',
      'isolator-details': editOpen ? 'edit-hardware' : 'report-hardware',
      'consent-captured': editOpen ? 'edit-consent' : 'report-consent',
      'hardware-photos': 'report-photos',
      'checklist-photos': editOpen ? 'edit-checklist' : 'report-checklist',
      'missing-clause-photos': editOpen ? 'edit-checklist' : 'report-checklist',
      'photo-timestamps': 'report-photos',
    };
    const anchor = target[ruleId];
    if (anchor) jumpTo(anchor);
  }

  async function applyCasePrefill() {
    if (!data || !editDraft) return;
    setEditBusy(true);
    setEditError(null);
    setEditInfo(null);
    try {
      const current = editDraft;
      const inspectionJob = await fetchCase(jobId);
      const ownerName = [inspectionJob.owner.firstName, inspectionJob.owner.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      const siteAddress = formatJobAddress(inspectionJob.address);
      const tenderer = tendererFromClient(inspectionJob.clientId);
      const patched: FieldReport = {
        ...current,
        cover: {
          ...current.cover,
          ...(ownerName ? { customerName: ownerName } : {}),
          ...(siteAddress ? { siteAddress } : {}),
          ...(tenderer ? { tenderer } : {}),
          ...(!current.cover.updatedOwnerEmail && inspectionJob.owner.email
            ? { updatedOwnerEmail: inspectionJob.owner.email }
            : {}),
        },
        consent: {
          ...current.consent,
          ...(ownerName && !current.consent.homeownerName ? { homeownerName: ownerName } : {}),
        },
        hardware: inspectionJob.hardware
          ? mergeHardwarePrefill(current.hardware, inspectionJob.hardware)
          : current.hardware,
      };
      setEditDraft(patched);
      setEditInfo(
        'Applied owner, email, site, client and available CER hardware data. Existing report values were kept.',
      );
    } catch (e) {
      setEditError(apiErrorReason(e));
    } finally {
      setEditBusy(false);
    }
  }

  /** Write the derived window into the draft. Never applied automatically. */
  function applyPhotoWindow(): void {
    if (!editDraft || !photoWindow?.sufficient) return;
    setEditDraft({
      ...editDraft,
      cover: {
        ...editDraft.cover,
        inspectionStartedAt: photoWindow.startedAt,
        inspectionEndedAt: photoWindow.endedAt,
      },
    });
    setEditInfo(
      `Inspection times set from ${photoWindow.photoCount} photo${photoWindow.photoCount === 1 ? '' : 's'} (${photoWindow.firstPhotoAt.slice(11, 16)}–${photoWindow.lastPhotoAt.slice(11, 16)} captured, padded 10 min before and 5 after). Check them before saving.`,
    );
  }

  async function saveEditedRevision() {
    if (!editDraft) return;
    setEditBusy(true);
    setEditError(null);
    setEditInfo(null);
    try {
      const revision = await editReport(jobId, submittedAt, {
        submission: {
          ...editDraft,
          hardware: calculateArrayTotals(seedBtyBatteryRow(editDraft.jobId, editDraft.hardware)),
        },
        hardwareChecks: photoChecks.filter((c) =>
          hardwareCheckSelectionCurrent(editDraft.hardware, c),
        ),
        ...(editNote.trim() ? { note: editNote.trim() } : {}),
      });
      // Saved — the draft has served its purpose and would otherwise be
      // offered back on top of the revision it became.
      clearDraft(jobId, submittedAt);
      setRecoverable(null);
      setEditDraft(null);
      setEditOpen(false);
      void navigate(
        `/reports/${encodeURIComponent(revision.jobId)}/${encodeURIComponent(
          revision.submittedAt,
        )}`,
      );
    } catch (e) {
      setEditError(apiErrorReason(e));
    } finally {
      setEditBusy(false);
    }
  }

  function stripPhotoFromDraft(photoId: string) {
    setEditDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        photos: current.photos.filter((p) => p.id !== photoId),
        checklist: current.checklist.map((clause) => ({
          ...clause,
          photoIds: clause.photoIds.filter((id) => id !== photoId),
        })),
      };
    });
  }

  async function uploadEditorPhoto(promptKey: string, file: File) {
    await uploadQaPhoto(jobId, { file, promptKey, photoId: crypto.randomUUID() });
    setPhotos(await fetchPhotos(jobId));
  }

  async function deletePhoto(photoId: string) {
    if (
      !window.confirm(
        'Delete this photo from the job? This saves immediately — it is not part of the report revision batch.',
      )
    ) {
      return;
    }
    setPhotoActionBusyId(photoId);
    setPhotoActionError(null);
    try {
      await deleteReportPhoto(jobId, photoId);
      stripPhotoFromDraft(photoId);
      if (refilingPhotoId === photoId) setRefilingPhotoId(null);
      load();
    } catch (e) {
      setPhotoActionError(e instanceof Error ? e.message : 'Could not delete that photo.');
    } finally {
      setPhotoActionBusyId(null);
    }
  }

  async function rotatePhoto(photoId: string, degrees: PhotoQuarterTurn) {
    setPhotoActionBusyId(photoId);
    setPhotoActionError(null);
    try {
      const manifest = await rotateReportPhoto(jobId, photoId, degrees);
      setPhotos((current) =>
        current.map((photo) => (photo.photoId === photoId ? manifest : photo)),
      );
      // The tile refreshes from the new uploadedAt. Only the open lightbox
      // needs a fresh object URL, and a failed preview must not undo the save.
      if (lightbox?.photoId !== photoId) return;
      try {
        const url = await fetchPhotoBlobUrl(jobId, photoId);
        setLightbox((current) => {
          if (!current || current.photoId !== photoId) {
            if (url.startsWith('blob:')) URL.revokeObjectURL(url);
            return current;
          }
          if (current.ownsBlob && current.blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(current.blobUrl);
          }
          return {
            ...current,
            blobUrl: url,
            filename: manifest.filename,
            mimeType: manifest.mimeType,
            ownsBlob: url.startsWith('blob:'),
          };
        });
      } catch {
        setPhotoActionError(
          'The photo was rotated, but the preview could not be refreshed. Close and reopen it.',
        );
      }
    } catch (e) {
      setPhotoActionError(apiErrorReason(e));
    } finally {
      setPhotoActionBusyId(null);
    }
  }

  function closeLightbox() {
    setLightbox((current) => {
      if (current?.ownsBlob && current.blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(current.blobUrl);
      }
      return null;
    });
  }

  async function refilePhoto(photoId: string, promptKey: string) {
    setPhotoActionBusyId(photoId);
    setPhotoActionError(null);
    try {
      const manifest = await rekeyReportPhoto(jobId, photoId, promptKey);
      setPhotos((current) =>
        current.map((photo) => (photo.photoId === photoId ? manifest : photo)),
      );
      setEditDraft((current) =>
        current
          ? applyPhotoRefile(current, photoId, manifest.promptKey, manifest.filename)
          : current,
      );
      setRefilingPhotoId(null);
      load();
    } catch (e) {
      setPhotoActionError(e instanceof Error ? e.message : 'Could not re-file that photo.');
    } finally {
      setPhotoActionBusyId(null);
    }
  }

  const openLightbox = (manifest: StoredPhotoManifest, blobUrl: string) => {
    if (manifest.mimeType === 'application/pdf') {
      // The thumbnail owns this URL; keep its existing cleanup lifecycle.
      void openFileInNewTab(() => Promise.resolve(blobUrl), false).catch((error) =>
        setPhotoActionError(apiErrorReason(error)),
      );
      return;
    }
    setLightbox({
      photoId: manifest.photoId,
      blobUrl,
      promptKey: photoPromptLabel(manifest.promptKey),
      capturedAt: manifest.capturedAt,
      ...(manifest.captureTimeSource === 'upload' ? { captureTimeUnknown: true } : {}),
      filename: manifest.filename,
      mimeType: manifest.mimeType,
    });
  };

  if (loadError) {
    return (
      <div className="screen">
        <p>
          <Link to="/" className="back-link">
            ← Back to reports
          </Link>
        </p>
        <p className="error">{loadError}</p>
        <button type="button" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="screen">
        <p className="loading">Loading report…</p>
      </div>
    );
  }

  const { submission, status, audit } = data;
  const roles = getSession()?.roles ?? [];
  const canQa = roles.some((role) => role === 'admin' || role === 'qa');
  const canEdit = canQa || roles.includes('inspector');
  const presentPromptKeys = promptKeysOnReport(submission.photos, photos);
  const photoRevision = photos
    .map((photo) => `${photo.photoId}:${photo.promptKey}:${photo.uploadedAt}`)
    .join('|');
  const readiness = fieldReadiness(
    countOutstandingNow(submission.outstandingAtSubmit, presentPromptKeys),
  );
  const allowed = ADMIN_TRANSITIONS[status];
  const canApprove = allowed.includes('approved');
  const canRequestInformation = allowed.includes('information-required');
  const canRequireRevisit = allowed.includes('revisit-required');
  const canRevertPending = allowed.includes('pending');

  return (
    <ReportSidebarLayout
      submission={editDraft ?? submission}
      showRoutine={showRoutine}
      onShowRoutine={setShowRoutine}
    >
      <div className="screen__header">
        <Link to="/" className="back-link">
          ← Back
        </Link>
        <div>
          <h2>
            {jobId} <StatusPill status={status} portalApprovedAt={data.portalApprovedAt} />{' '}
            <ReadinessPill readiness={readiness} />
          </h2>
          <div className="subtitle">
            Submitted {formatDateTime(data.inspectorSubmittedAt ?? submission.submittedAt)} by{' '}
            {submission.inspectorId}
            {data.portalApprovedAt
              ? ` · Approved for portal ${formatDateTime(data.portalApprovedAt)}`
              : ''}
            {(data.inspectorSubmittedAt ?? submission.submittedAt) !== data.submittedAt
              ? ` · This revision ${formatDateTime(data.submittedAt)}`
              : ''}
          </div>
        </div>
      </div>

      {!!data.hardwarePhotoReviewWarnings?.length && (
        <div className="card" role="status">
          <strong>Saved — photo verification needs review</strong>
          {data.hardwarePhotoReviewWarnings.map((warning, index) => (
            <p key={index}>{warning}</p>
          ))}
        </div>
      )}

      {(!!data.adminMergeConflicts?.length || firstSubmissionMerge) && (
        <div className="card" role="status">
          <strong>
            {firstSubmissionMerge
              ? 'Field submission merged with dashboard hardware'
              : 'Field resubmit kept admin values'}
          </strong>
          <p>
            {firstSubmissionMerge
              ? 'The first field submission was merged with dashboard-entered hardware.'
              : 'The inspector submitted again after dashboard edits.'}{' '}
            Dashboard-entered values were kept pending review of disagreements.
          </p>
          {data.adminMergeConflicts?.map((conflict) => (
            <p key={conflict}>{conflict}</p>
          ))}
        </div>
      )}

      {/*
       * Every action lives here, in one bar, in the order the work happens:
       * Edit → Return → QA passed → Approve for Portal.
       *
       * "Finalise" and "Approve for Portal" used to be one word — "Approve" —
       * on two different controls: this bar transitioned the status, while a
       * button buried in the QA panel wrote the portal payload. They read as
       * the same action and are not: only the second makes a report reachable
       * by the extension. A report was approved twice and still could not be
       * pushed, because neither click was the one that mattered.
       *
       * So they are named for what they do, and sit next to each other where
       * the difference is visible.
       */}
      <div className="action-bar">
        <div className="action-bar__current">
          <span>Current status:</span>
          <StatusPill status={status} portalApprovedAt={data.portalApprovedAt} />
        </div>
        {canEdit && (
          <button type="button" disabled={busy || editBusy} onClick={() => openEditor()}>
            Edit
          </button>
        )}
        {canQa && (
          <button
            type="button"
            className="danger"
            disabled={(!canRequestInformation && !canRequireRevisit) || busy}
            onClick={() => setSendBackOpen(true)}
          >
            Return
          </button>
        )}
        {canQa && canRevertPending && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void doTransition('pending')}
            title="Reopen review — put this report back in the QA queue."
          >
            Reopen review
          </button>
        )}
        {canQa && (
          <button
            type="button"
            className="success"
            disabled={!canApprove || busy || approvalConflictCount > 0}
            onClick={() => void doTransition('approved')}
            title={
              approvalConflictCount
                ? `Resolve ${approvalConflictCount} hardware conflict(s) on the latest revision before recording QA passed.`
                : 'Record that QA passed this report.'
            }
          >
            QA passed
          </button>
        )}
        {canQa && (
          <button
            type="button"
            className="primary"
            disabled={portalBusy || busy || hasUnsavedEdits || approvalConflictCount > 0}
            onClick={() => void doApproveForPortal()}
            title={
              approvalConflictCount
                ? `Resolve ${approvalConflictCount} hardware conflict(s) on the latest revision before approving.`
                : 'Make this revision available to the portal extension.'
            }
          >
            {portalBusy ? 'Approving…' : 'Approve for Portal'}
          </button>
        )}
        {canQa && labellingUnsetOnBty && (
          <button
            type="button"
            className="chip chip--warn"
            title={LABELLING_UNCONFIRMED_BANNER}
            onClick={showLabellingSelect}
          >
            {LABELLING_CHIP_LABEL}
          </button>
        )}
      </div>

      {!isLatestRevision && latestHardware?.submittedAt && (
        <p className="card">
          Hardware conflicts are reviewed on the{' '}
          <Link
            to={`/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(latestHardware.submittedAt)}`}
          >
            latest revision
          </Link>{' '}
          ({approvalConflictCount} unresolved). This revision is read-only for conflict resolution.
        </p>
      )}
      {canQa && hardwareContextStatus === 'failed' && unresolvedConflicts.length > 0 && (
        <p className="card">
          Couldn't confirm this is the latest revision — reload to resolve conflicts
        </p>
      )}
      {canQa && (isLatestRevision || hardwareContextStatus === 'failed') && (
        <HardwareConflictsPanel
          conflicts={data.hardwareConflicts ?? []}
          fieldSubmittedAt={data.inspectorSubmittedAt ?? data.submission.submittedAt}
          disabled={busy || hasUnsavedEdits || !isLatestRevision}
          onResolve={(id, choice, value) => {
            setBusy(true);
            void resolveHardwareConflict(jobId, data.submittedAt, id, choice, value)
              .then(({ revision }) => {
                void navigate(
                  `/reports/${encodeURIComponent(jobId)}/${encodeURIComponent(revision.submittedAt)}`,
                );
              })
              .catch((e) => setActionError(apiErrorReason(e)))
              .finally(() => setBusy(false));
          }}
        />
      )}
      <nav className="report-section-nav" aria-label="Report sections">
        <button
          type="button"
          className="link-button"
          onClick={() => jumpTo(editOpen ? 'edit-cover' : 'report-cover')}
        >
          Cover
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => jumpTo(editOpen ? 'edit-consent' : 'report-consent')}
        >
          Consent
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => jumpTo(editOpen ? 'edit-checklist' : 'report-checklist')}
        >
          Checklist
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => jumpTo(editOpen ? 'edit-hardware' : 'report-hardware')}
        >
          Hardware
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => jumpTo(editOpen ? 'edit-photos' : 'report-photos')}
        >
          Photos
        </button>
      </nav>
      {canQa && hasUnsavedEdits && (
        <p className="banner" role="status">
          Save your edits before approving for the portal.{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              if (editDraft) setEditOpen(true);
              else restoreDraft();
            }}
          >
            Resume editing
          </button>
        </p>
      )}
      {portalApproved && (
        <p className="success">
          Approved for Portal — the extension can now fill the CER portal from this report.
        </p>
      )}
      {portalSuperseded && (
        <div className="banner banner--warning">
          <strong>This is not the newest revision.</strong> A later report was submitted at{' '}
          {formatDateTime(portalSuperseded)}.
          <p>Approving this replaces the version available to the portal extension.</p>
          <button
            type="button"
            disabled={portalBusy || approvalConflictCount > 0}
            onClick={() => {
              setSupersededAccepted(true);
              void doApproveForPortal({ approveSuperseded: true });
            }}
          >
            {portalBusy ? 'Approving…' : 'Approve this older revision anyway'}
          </button>
          <button type="button" disabled={portalBusy} onClick={() => setPortalSuperseded(null)}>
            Cancel
          </button>
        </div>
      )}
      {missingPhotoGate && (
        <MissingPhotoOverrideDialog
          items={missingPhotoGate.items}
          report={missingPhotoGate.report}
          busy={portalBusy}
          onCancel={() => setMissingPhotoGate(null)}
          onConfirm={(reason) => {
            const otherFindings = missingPhotoGate.report.results.some(
              (result) => result.ruleId !== 'missing-clause-photos' && result.status !== 'pass',
            );
            void doApproveForPortal({
              force: otherFindings,
              missingPhotoOverride: { acknowledged: true, reason },
            });
          }}
        />
      )}
      {portalBlocked && (
        <div className="banner banner--warning">
          <strong>QA {portalBlocked.overall === 'fail' ? 'is failing' : 'has warnings'}:</strong>{' '}
          {portalBlocked.results
            .filter((r) => r.status === portalBlocked.overall)
            .map((r) => r.label)
            .join(', ')}
          .
          <p>
            Review these findings before overriding QA. The CER portal also validates the
            submission.
          </p>
          <p>{QA_OVERRIDE_LABELLING_NOTE}</p>
          <button
            type="button"
            disabled={portalBusy || approvalConflictCount > 0}
            onClick={() => void doApproveForPortal({ force: true })}
          >
            {portalBusy ? 'Approving…' : 'Approve for Portal anyway'}
          </button>
          <button type="button" disabled={portalBusy} onClick={() => setPortalBlocked(null)}>
            Cancel
          </button>
        </div>
      )}
      {portalLabellingBlocked && (
        <div className="banner banner--warning" role="alert">
          <strong>Switchboard labelling not confirmed.</strong>
          <p>{LABELLING_UNCONFIRMED_BANNER}</p>
          <p>
            This is a hard gate (CER-69). QA force and Approve for Portal anyway cannot bypass it.
          </p>
          <button type="button" className="link-button" onClick={showLabellingSelect}>
            Show me
          </button>
          <button
            type="button"
            disabled={portalBusy}
            onClick={() => setPortalLabellingBlocked(false)}
          >
            Dismiss
          </button>
        </div>
      )}

      {actionError && <p className="error">{actionError}</p>}
      {recoverable && !editOpen && (
        <div className="banner banner--warning">
          <strong>You have unsaved edits</strong> from {formatDateTime(recoverable.savedAt)}, kept
          in this browser. They were never sent to the server.
          <div className="banner__actions">
            <button type="button" onClick={restoreDraft}>
              Resume editing
            </button>
            <button type="button" className="link-button" onClick={discardDraft}>
              Discard them
            </button>
          </div>
        </div>
      )}
      {editOpen && editDraft && (
        <div className="card edit-panel">
          <div className="edit-panel__head">
            <div>
              <h3>Edit report revision</h3>
              <p className="subtitle">
                Save creates a new revision. Photo changes save immediately.
              </p>
            </div>
            <button type="button" onClick={() => setEditOpen(false)} disabled={editBusy}>
              Close
            </button>
          </div>
          {editError && <p className="error">{editError}</p>}
          {editInfo && <p className="banner banner--success">{editInfo}</p>}
          <details className="report-help">
            <summary>About saving</summary>
            <p>
              Your draft is kept in this browser. Save as a new revision to send changes for QA.
              Uploading, deleting and filing photos takes effect immediately.
            </p>
          </details>
          <div className="edit-panel__actions">
            <button
              type="button"
              className="success"
              disabled={editBusy}
              onClick={() => void saveEditedRevision()}
            >
              {editBusy ? 'Saving…' : 'Save as new revision'}
            </button>
            <button type="button" onClick={() => void applyCasePrefill()} disabled={editBusy}>
              Pre-fill from job
            </button>
          </div>
          <label>
            <span>Edit note</span>
            <input
              type="text"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              placeholder="e.g. Corrected SB & Labelling 13 assessment and battery model"
            />
          </label>
          <div className="edit-form-section" id="edit-timing">
            <h4>Consent/timing</h4>
            <div className="form-grid">
              {' '}
              <label>
                Inspection started
                <input
                  type="datetime-local"
                  value={coverTimeForInput(editDraft.cover.inspectionStartedAt)}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, inspectionStartedAt: e.target.value },
                    })
                  }
                  onInput={() => setWindowSuggested(false)}
                />
              </label>
              <label>
                Inspection ended
                <input
                  type="datetime-local"
                  value={coverTimeForInput(editDraft.cover.inspectionEndedAt)}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, inspectionEndedAt: e.target.value },
                    })
                  }
                  onInput={() => setWindowSuggested(false)}
                />
              </label>
            </div>{' '}
            {/* The photos are better evidence of when the inspection happened
                than a typed time — offer that answer rather than making the
                reviewer reconcile two numbers by hand. Suggested, never
                applied automatically: a derived time must not masquerade as
                one the inspector recorded. */}
            <div className={`photo-window${windowSuggested ? ' photo-window--applied' : ''}`}>
              {photoWindow && !photoWindow.sufficient ? (
                <span className="muted">
                  {photoWindow.photoCount < 2
                    ? `Only ${photoWindow.photoCount} photo on this job carries a capture time`
                    : `All ${photoWindow.photoCount} photo timestamps share one instant`}
                  , so the inspection window cannot be derived — a single-photo inspection is not
                  possible, so the photo set is still to come. Attach the rest from the Dropbox
                  panel, or enter the times by hand.
                </span>
              ) : photoWindow ? (
                <>
                  <span>
                    {windowSuggested ? <strong>Filled from photos. </strong> : null}
                    Photos imply <strong>{photoWindow.startedAt.slice(11, 16)}</strong>–
                    <strong>{photoWindow.endedAt.slice(11, 16)}</strong> on{' '}
                    {photoWindow.startedAt.slice(0, 10)} — from {photoWindow.photoCount} photo
                    {photoWindow.photoCount === 1 ? '' : 's'}, padded 10 min before and 5 after
                    {photoWindow.outliersDropped > 0
                      ? `, ignoring ${photoWindow.outliersDropped} from another day`
                      : ''}
                    .
                  </span>
                  <button type="button" className="ghost-btn" onClick={applyPhotoWindow}>
                    {windowSuggested ? 'Re-apply photo times' : 'Use photo times'}
                  </button>
                </>
              ) : (
                <span className="muted">
                  No photo on this report carries a capture time, so the inspection window cannot be
                  derived.
                </span>
              )}
            </div>
          </div>
          <div className="edit-form-section" id="edit-cover">
            <h4>Cover sheet</h4>
            <div className="form-grid">
              <label>
                Owner name
                <input
                  value={editDraft.cover.customerName ?? ''}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, customerName: e.target.value },
                    })
                  }
                />
              </label>
              <label>
                Site address
                <input
                  value={editDraft.cover.siteAddress ?? ''}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, siteAddress: e.target.value },
                    })
                  }
                />
              </label>
              <label>
                Client
                <select
                  value={editDraft.cover.tenderer ?? ''}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: {
                        ...editDraft.cover,
                        tenderer: (e.target.value || undefined) as FieldReport['cover']['tenderer'],
                      },
                    })
                  }
                >
                  <option value="">Select…</option>
                  {['Kerfoot', 'ITP', 'Smart Energy', 'MEA', 'GSES', 'Tech Safe'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Inspector ID card
                <input
                  value={editDraft.cover.inspectorIdCardNumber ?? ''}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, inspectorIdCardNumber: e.target.value },
                    })
                  }
                />
              </label>
            </div>
            <div className="check-row">
              <label>
                Roof cavity inspected
                <select
                  value={
                    editDraft.cover.roofCavityInspected === undefined
                      ? ''
                      : String(editDraft.cover.roofCavityInspected)
                  }
                  onChange={(e) =>
                    setEditDraft(
                      withRoofCavity(editDraft, {
                        roofCavityInspected:
                          e.target.value === '' ? undefined : e.target.value === 'true',
                      }),
                    )
                  }
                >
                  <option value="">Not recorded</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              {editDraft.cover.roofCavityInspected === false && (
                <>
                  {Object.entries(ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS).map(([key, label]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={
                          editDraft.cover.roofCavityNotInspectedReasons?.includes(
                            key as RoofCavityNotInspectedReason,
                          ) ?? false
                        }
                        onChange={(e) =>
                          setEditDraft(
                            withRoofCavity(editDraft, {
                              roofCavityNotInspectedReasons: e.target.checked
                                ? [
                                    ...(editDraft.cover.roofCavityNotInspectedReasons ?? []),
                                    key as RoofCavityNotInspectedReason,
                                  ]
                                : editDraft.cover.roofCavityNotInspectedReasons?.filter(
                                    (reason) => reason !== key,
                                  ),
                            }),
                          )
                        }
                      />
                      {label}
                    </label>
                  ))}
                  <label>
                    Other roof cavity reason
                    <textarea
                      value={editDraft.cover.roofCavityNotInspectedNote ?? ''}
                      onChange={(e) =>
                        setEditDraft(
                          withRoofCavity(editDraft, { roofCavityNotInspectedNote: e.target.value }),
                        )
                      }
                    />
                  </label>
                </>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={editDraft.cover.microinverterSystem ?? false}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, microinverterSystem: e.target.checked },
                    })
                  }
                />{' '}
                Microinverter system
              </label>
            </div>
          </div>
          <div className="edit-form-section" id="edit-consent">
            <h4>Consent and comments</h4>
            <div className="form-grid">
              <label>
                Consent provider name
                <input
                  value={editDraft.consent.homeownerName}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      consent: { ...editDraft.consent, homeownerName: e.target.value },
                    })
                  }
                />
              </label>
              <label>
                Consent provider email
                <input
                  type="email"
                  value={editDraft.cover.consentProviderEmail ?? ''}
                  onChange={(e) =>
                    setEditDraft({
                      ...editDraft,
                      cover: { ...editDraft.cover, consentProviderEmail: e.target.value },
                    })
                  }
                />
              </label>
            </div>
            <label>
              Updated owner email address
              <input
                type="email"
                value={editDraft.cover.updatedOwnerEmail ?? ''}
                onChange={(e) =>
                  setEditDraft({
                    ...editDraft,
                    cover: { ...editDraft.cover, updatedOwnerEmail: e.target.value },
                  })
                }
              />
            </label>
            <label>
              General comments
              <textarea
                rows={3}
                value={editDraft.cover.generalComments ?? ''}
                onChange={(e) =>
                  setEditDraft({
                    ...editDraft,
                    cover: { ...editDraft.cover, generalComments: e.target.value },
                  })
                }
              />
            </label>
          </div>
          <label>
            General notes for reviewer
            <textarea
              rows={3}
              value={editDraft.generalNotes ?? ''}
              onChange={(e) => setEditDraft({ ...editDraft, generalNotes: e.target.value })}
            />
          </label>
          <h4>Signed consent forms</h4>
          <ReportPhotoEditor
            jobId={jobId}
            promptKey="consent-signed-form"
            photos={photos.filter((photo) => photo.promptKey === 'consent-signed-form')}
            accept="image/*,application/pdf"
            uploadLabel="Add signed consent form"
            onOpenPhoto={openLightbox}
            onUploadPhoto={uploadEditorPhoto}
            onDeletePhoto={deletePhoto}
          />
          <p className="muted">Photo uploads, rotations and deletions save immediately.</p>
          <div id="edit-photos" />
          <label>
            Inspector general recommendations
            <textarea
              rows={5}
              value={
                editDraft.generalRecommendations ?? editDraft.cover.inspectorRecommendation ?? ''
              }
              onChange={(e) =>
                setEditDraft({
                  ...editDraft,
                  generalRecommendations: e.target.value,
                })
              }
            />
          </label>
          <h4>General recommendation photos</h4>
          <ReportPhotoEditor
            jobId={jobId}
            promptKey={GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY}
            photos={photos.filter(
              (photo) => photo.promptKey === GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY,
            )}
            onOpenPhoto={openLightbox}
            onUploadPhoto={uploadEditorPhoto}
            onDeletePhoto={deletePhoto}
          />
          <details>
            <summary>Common general recommendations</summary>
            {generalRecommendationWording(jobId).map((text) => (
              <button
                type="button"
                key={text}
                onClick={() => {
                  const current =
                    editDraft.generalRecommendations ??
                    editDraft.cover.inspectorRecommendation ??
                    '';
                  setEditDraft({
                    ...editDraft,
                    generalRecommendations: current.includes(text)
                      ? current.replace(text, '').trim()
                      : [current.trim(), text].filter(Boolean).join('\n\n'),
                  });
                }}
              >
                {text}
              </button>
            ))}
          </details>
          <div id="edit-hardware" />
          <HardwarePhotoCheck
            report={data}
            hardware={editDraft.hardware}
            photos={photos}
            disabled={editBusy}
            dismissedIds={dismissedPhotoReviewIds}
            onDismissedIdsChange={setDismissedPhotoReviewIds}
            onSeedEmptyBattery={onSeedEmptyBattery}
            onAcceptOnlyReviews={onAcceptOnlyReviews}
            onApply={(hardware, selections) => {
              setEditDraft((current) =>
                current ? { ...current, hardware: calculateArrayTotals(hardware) } : current,
              );
              setPhotoChecks((current) => mergeHardwareCheckSelections(current, selections));
              setEditInfo('Photo readings added to your draft. Save when ready.');
            }}
          />
          <ReportHardwareEditor
            conflicts={isLatestRevision ? (data.hardwareConflicts ?? []) : []}
            draft={editDraft}
            batteryInstallationType={batteryInstallationType}
            suggestionReviewCount={acceptOnlyReviews.length}
            suggestionReviewFields={acceptOnlyReviews.map((item) =>
              item.surface === 'switchboard'
                ? `switchboard:${item.field}`
                : `${item.kind}:${item.field}`,
            )}
            onChange={setEditDraft}
            onRowRemoved={(kind, index) =>
              setPhotoChecks((current) => reindexHardwareCheckSelections(current, kind, index))
            }
            onUploadPhoto={uploadEditorPhoto}
            onDeletePhoto={deletePhoto}
            photos={photos}
            onOpenPhoto={openLightbox}
            {...(canQa
              ? { onRotatePhoto: (photoId, degrees) => rotatePhoto(photoId, degrees) }
              : {})}
          />
          <div id="edit-checklist" />
          <ReportChecklistEditor
            draft={editDraft}
            onChange={setEditDraft}
            showRoutine={showRoutine}
            onShowRoutine={setShowRoutine}
            onUploadPhoto={uploadEditorPhoto}
            onDeletePhoto={deletePhoto}
            photos={photos}
            onOpenPhoto={openLightbox}
            {...(editFocusKey !== null ? { focusKey: editFocusKey } : {})}
            {...(canQa
              ? {
                  canManagePhotos: true,
                  photoActionBusyId,
                  refilingPhotoId,
                  filingTargets: filingTargetsFor(editDraft),
                  onToggleRefile: (photoId: string) =>
                    setRefilingPhotoId(refilingPhotoId === photoId ? null : photoId),
                  onRotatePhoto: (photoId: string, degrees: PhotoQuarterTurn) =>
                    rotatePhoto(photoId, degrees),
                  onRefilePhoto: (photoId: string, promptKey: string) =>
                    refilePhoto(photoId, promptKey),
                }
              : {})}
          />
          {jobId.startsWith('PVD') && (
            <div className="edit-form-section">
              <h4>Non-checklist items</h4>
              {[...PV_NON_CHECKLIST_ITEMS].map(([id, item]) => (
                <label key={id}>
                  {id} — {item.description}
                  <textarea
                    rows={2}
                    value={editDraft.nonChecklistResponses?.[id] ?? ''}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        nonChecklistResponses: {
                          ...editDraft.nonChecklistResponses,
                          [id]: e.target.value,
                        },
                      })
                    }
                  />
                </label>
              ))}
            </div>
          )}
          <p className="muted">
            Signatures stay on the original consent record — send the job back if a signature needs
            re-capturing.
          </p>
          <div className="edit-panel__save">
            <button
              type="button"
              className="success"
              disabled={editBusy}
              onClick={() => void saveEditedRevision()}
            >
              {editBusy ? 'Saving…' : 'Save as new revision'}
            </button>
            <button
              type="button"
              className="link-button"
              disabled={editBusy}
              onClick={discardDraft}
            >
              Discard edits
            </button>
          </div>
        </div>
      )}

      <div className="detail-grid">
        {/* Pre-upload QA ─────────────────────────────────────────────── */}
        {canQa && (
          <QaPanel
            inspectorName={submission.inspectorId}
            inspectorCard={submission.cover.inspectorIdCardNumber}
            jobId={jobId}
            submittedAt={submittedAt}
            outstandingAtSubmit={submission.outstandingAtSubmit}
            presentPromptKeys={presentPromptKeys}
            photoRevision={photoRevision}
            onShowRule={showRule}
          />
        )}

        {/* The job's Dropbox folder. Legacy imports open expanded — for those
            the folder IS the evidence the report was built from. */}
        {canQa && (
          <DropboxPanel
            jobId={jobId}
            submission={submission}
            autoLoad={submission.deviceId === 'legacy-dropbox-import'}
            onAttached={load}
          />
        )}

        {!editOpen && (
          <>
            <div className="card">
              <h3>Consent/timing</h3>
              <dl>
                <Row
                  label="Inspection start"
                  value={formatDateTime(submission.cover.inspectionStartedAt)}
                />
                <Row
                  label="Inspection end"
                  value={formatDateTime(submission.cover.inspectionEndedAt)}
                />
                <Row label="Consent signed" value={formatDateTime(submission.consent.signedAt)} />
              </dl>
            </div>
            {/* Cover sheet ───────────────────────────────────────────────── */}
            <div className="card" id="report-cover">
              <h3>Cover sheet</h3>
              <dl>
                <Row label="Job ID" value={submission.cover.jobId} />
                <Row label="Inspector" value={submission.inspectorId} />
                <Row label="Started" value={formatDateTime(submission.startedAt)} />
                <Row
                  label="Submitted"
                  value={formatDateTime(data.inspectorSubmittedAt ?? submission.submittedAt)}
                />
                {(data.inspectorSubmittedAt ?? submission.submittedAt) !== data.submittedAt && (
                  <Row label="This revision" value={formatDateTime(data.submittedAt)} />
                )}
                {data.portalApprovedAt && (
                  <Row label="Approved for portal" value={formatDateTime(data.portalApprovedAt)} />
                )}
                <Row
                  label="Inspection start"
                  value={formatDateTime(submission.cover.inspectionStartedAt)}
                />
                <Row
                  label="Inspection end"
                  value={formatDateTime(submission.cover.inspectionEndedAt)}
                />
                <Row label="Owner" value={submission.cover.customerName ?? '—'} />
                <Row label="Site address" value={submission.cover.siteAddress ?? '—'} />
                <Row label="Client" value={submission.cover.tenderer ?? '—'} />
                <Row
                  label="Roof cavity inspected"
                  value={
                    submission.cover.roofCavityInspected === undefined
                      ? 'Not recorded'
                      : submission.cover.roofCavityInspected
                        ? 'Yes'
                        : 'No'
                  }
                />
                {submission.cover.roofCavityInspected === false && (
                  <>
                    <Row
                      label="Not inspected because"
                      value={
                        (submission.cover.roofCavityNotInspectedReasons ?? [])
                          .map((reason) => ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS[reason])
                          .join('; ') || 'Not recorded'
                      }
                    />
                    {submission.cover.roofCavityNotInspectedNote && (
                      <Row
                        label="Roof cavity note"
                        value={submission.cover.roofCavityNotInspectedNote}
                      />
                    )}
                  </>
                )}
              </dl>
            </div>

            <div className="card">
              <h3>Consent and comments</h3>
              <dl>
                <Row label="Consent provider" value={submission.consent.homeownerName} />
                <Row label="General comments" value={submission.cover.generalComments ?? '—'} />
              </dl>
            </div>
            {/* Consent ───────────────────────────────────────────────────── */}
            <div className="card" id="report-consent">
              {canQa && (
                <label>
                  Upload signed consent form (PDF or scan)
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    disabled={qaPhotoBusy}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      setQaPhotoBusy(true);
                      setQaPhotoError(null);
                      void uploadQaPhoto(jobId, {
                        photoId: crypto.randomUUID(),
                        promptKey: 'consent-signed-form',
                        file,
                      })
                        .then(() => load())
                        .catch((err: unknown) =>
                          setQaPhotoError(err instanceof Error ? err.message : 'Upload failed'),
                        )
                        .finally(() => setQaPhotoBusy(false));
                    }}
                  />
                </label>
              )}
              {qaPhotoError && <p role="alert">{qaPhotoError}</p>}
              {photoActionError && <p role="alert">{photoActionError}</p>}
              {photos
                .filter((p) => p.promptKey === 'consent-signed-form')
                .map((p) => (
                  <div key={p.photoId}>
                    <button
                      type="button"
                      onClick={() =>
                        void openFileInNewTab(() => fetchPhotoBlobUrl(jobId, p.photoId)).catch(
                          (error) => setQaPhotoError(apiErrorReason(error)),
                        )
                      }
                    >
                      {p.filename}
                    </button>
                    {canQa && (
                      <button
                        type="button"
                        disabled={photoActionBusyId === p.photoId}
                        onClick={() => void deletePhoto(p.photoId)}
                      >
                        Remove consent file
                      </button>
                    )}
                  </div>
                ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <h3>Signed consent forms</h3>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    setActionError(null);
                    void openSignedConsentPdf(jobId, submittedAt)
                      .catch((error) => setActionError(apiErrorReason(error)))
                      .finally(() => setBusy(false));
                  }}
                >
                  View signed consent PDF
                </button>
              </div>
              <dl>
                <Row label="Homeowner name" value={submission.consent.homeownerName} />
                <Row label="Signed at" value={formatDateTime(submission.consent.signedAt)} />
                <Row label="Consent form version" value={submission.consent.consentFormVersion} />
              </dl>
              <div className="signature-pair">
                <div className="signature-block">
                  <h4>Homeowner signature</h4>
                  <img src={submission.consent.homeownerSignaturePng} alt="Homeowner signature" />
                </div>
              </div>
            </div>

            <div className="card">
              {submission.generalNotes && (
                <section>
                  <h3>General notes for reviewer</h3>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{submission.generalNotes}</p>
                </section>
              )}
              <h3>Inspector general recommendations</h3>
              <p style={{ whiteSpace: 'pre-wrap' }}>
                {submission.generalRecommendations ??
                  submission.cover.inspectorRecommendation ??
                  'No general recommendations recorded.'}
              </p>
              {canEdit && (
                <button type="button" onClick={() => openEditor()}>
                  Edit recommendations
                </button>
              )}
              <div className="photo-grid">
                {photos
                  .filter((photo) => photo.promptKey === GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY)
                  .map((photo) => (
                    <PhotoThumb
                      key={photo.photoId}
                      jobId={jobId}
                      photoId={photo.photoId}
                      promptKey={photo.promptKey}
                      caption={photo.filename}
                      mimeType={photo.mimeType}
                      version={photo.uploadedAt}
                      onOpen={(url) => openLightbox(photo, url)}
                    />
                  ))}
              </div>
            </div>
            {canEdit && (
              <div className="card">
                <h3>Check against photos</h3>
                <button
                  type="button"
                  onClick={() => {
                    openEditor();
                    requestAnimationFrame(() => jumpTo('edit-hardware'));
                  }}
                >
                  Check hardware photos
                </button>
                {canEdit && !editOpen && !recoverable && (
                  <HardwareLabelSuggestions
                    key={`${jobId}:${submittedAt}`}
                    report={data}
                    photos={photos}
                    onApplied={(revision) => {
                      void navigate(
                        `/reports/${encodeURIComponent(revision.jobId)}/${encodeURIComponent(revision.submittedAt)}`,
                      );
                    }}
                  />
                )}
              </div>
            )}
            {/* Hardware ──────────────────────────────────────────────────── */}
            <HardwareConflictsContext.Provider
              value={isLatestRevision ? (data.hardwareConflicts ?? []) : []}
            >
              <div className="card" id="report-hardware">
                <h3>Hardware and system data</h3>
                <HardwareConflictsContext.Provider
                  value={isLatestRevision ? (data.hardwareConflicts ?? []) : []}
                >
                  <HardwareConflictValues
                    conflicts={isLatestRevision ? (data.hardwareConflicts ?? []) : []}
                  />
                </HardwareConflictsContext.Provider>
                {data.hardwareLabelReviews?.map((review) => (
                  <p className="muted" key={review.suggestionId}>
                    Label readings accepted for inverter {review.inverterIndex + 1}:{' '}
                    {review.readings.map((r) => `${r.field}: ${r.value}`).join(', ')}. Reviewed by{' '}
                    {review.reviewedBy} on {new Date(review.reviewedAt).toLocaleString()}.
                  </p>
                ))}
                <h4>Array photos</h4>
                <div className="photo-grid">
                  {photos
                    .filter((p) => p.promptKey === 'pv-array-overview')
                    .map((p) => (
                      <PhotoThumb
                        key={p.photoId}
                        jobId={jobId}
                        photoId={p.photoId}
                        promptKey={p.promptKey}
                        caption={p.filename}
                        mimeType={p.mimeType}
                        version={p.uploadedAt}
                        onOpen={(url) => openLightbox(p, url)}
                      />
                    ))}
                </div>
                <h4>AC isolator photos — inverter and switchboard</h4>
                <div className="photo-grid">
                  {photos
                    .filter((p) =>
                      [
                        'pv-ac-isolator-rating',
                        'pv-main-switch-inverter-supply',
                        'isolators-ac-isolators-before-during-after',
                      ].includes(p.promptKey),
                    )
                    .map((p) => (
                      <PhotoThumb
                        key={p.photoId}
                        jobId={jobId}
                        photoId={p.photoId}
                        promptKey={p.promptKey}
                        caption={p.filename}
                        mimeType={p.mimeType}
                        version={p.uploadedAt}
                        onOpen={(url) => openLightbox(p, url)}
                      />
                    ))}
                </div>
                <dl>
                  <Row
                    path="panelsInLongestString"
                    label="Panels in longest string"
                    value={String(submission.hardware.panelsInLongestString ?? '—')}
                  />
                  <Row
                    path="numberOfStrings"
                    label="Number of strings"
                    value={String(submission.hardware.numberOfStrings ?? '—')}
                  />
                  <Row
                    path="totalArrayVoltageOpenCircuitV"
                    label="Total array open-circuit voltage (V)"
                    value={String(submission.hardware.totalArrayVoltageOpenCircuitV ?? '—')}
                  />
                  <Row
                    path="totalArrayCurrentShortCircuitA"
                    label="Total array short-circuit current (A)"
                    value={String(submission.hardware.totalArrayCurrentShortCircuitA ?? '—')}
                  />
                </dl>
                <h4 style={{ margin: '4px 0 8px 0', fontSize: 13, color: 'var(--fg-muted)' }}>
                  Inverters ({submission.hardware.inverters.length})
                </h4>
                {submission.hardware.inverters.length === 0 ? (
                  <p className="muted">No inverters captured.</p>
                ) : (
                  submission.hardware.inverters.map((inv, i) => (
                    <dl key={i}>
                      <Row path={`inverters[${i}].make`} label="Make" value={inv.make ?? '—'} />
                      <Row path={`inverters[${i}].model`} label="Model" value={inv.model ?? '—'} />
                      <Row
                        path={`inverters[${i}].acPowerRatingW`}
                        label="AC power (W)"
                        value={inv.acPowerRatingW !== undefined ? String(inv.acPowerRatingW) : '—'}
                      />
                    </dl>
                  ))
                )}

                <h4 style={{ margin: '20px 0 8px 0', fontSize: 13, color: 'var(--fg-muted)' }}>
                  Modules ({submission.hardware.modules.length})
                </h4>
                {submission.hardware.modules.length === 0 ? (
                  <p className="muted">No modules captured.</p>
                ) : (
                  submission.hardware.modules.map((mod, i) => (
                    <dl key={i}>
                      <Row path={`modules[${i}].make`} label="Make" value={mod.make ?? '—'} />
                      <Row path={`modules[${i}].model`} label="Model" value={mod.model ?? '—'} />
                      <Row
                        path={`modules[${i}].quantity`}
                        label="Quantity"
                        value={mod.quantity !== undefined ? String(mod.quantity) : '—'}
                      />
                      <Row
                        path={`modules[${i}].powerRatingW`}
                        label="Power (W)"
                        value={mod.powerRatingW !== undefined ? String(mod.powerRatingW) : '—'}
                      />
                    </dl>
                  ))
                )}

                <h4 style={{ margin: '20px 0 8px 0', fontSize: 13, color: 'var(--fg-muted)' }}>
                  Batteries ({submission.hardware.batteries.length})
                </h4>
                {submission.hardware.batteries.length === 0 ? (
                  <p className="muted">No batteries captured.</p>
                ) : (
                  submission.hardware.batteries.map((b, i) => (
                    <dl key={i}>
                      <Row path={`batteries[${i}].make`} label="Make" value={b.make ?? '—'} />
                      <Row path={`batteries[${i}].model`} label="Model" value={b.model ?? '—'} />
                      <Row path={`batteries[${i}].serial`} label="Serial" value={b.serial ?? '—'} />
                      <Row
                        path={`batteries[${i}].capacityKwh`}
                        label="Capacity (kWh)"
                        value={b.capacityKwh !== undefined ? String(b.capacityKwh) : '—'}
                      />
                    </dl>
                  ))
                )}
              </div>
            </HardwareConflictsContext.Provider>
            {/* Checklist ─────────────────────────────────────────────────── */}
            <div className="card" id="report-checklist">
              <h3>Checklist</h3>
              <label>
                <input
                  type="checkbox"
                  checked={showRoutine}
                  onChange={(e) => setShowRoutine(e.target.checked)}
                />{' '}
                Show compliant, not sighted and not applicable items
              </label>
              {clausesBySection.length === 0 && (
                <p className="muted">No clause responses captured.</p>
              )}
              {clausesBySection.map(([section, responses]) => {
                const visible = responses.filter(
                  (row) => showRoutine || !isRoutineChecklistResponse(row.response),
                );
                if (visible.length === 0) return null;
                return (
                  <div key={section} className="clause-section">
                    <h4>{SECTION_LABEL[section]}</h4>
                    {visible.map((row) =>
                      row.response ? (
                        <ClauseItem
                          key={`${section}:${row.clauseId}`}
                          clause={row.response}
                          question={row.question}
                          photoById={photoById}
                          canManage={canQa}
                          photoActionBusyId={photoActionBusyId}
                          refilingPhotoId={refilingPhotoId}
                          filingTargets={filingTargetsFor(submission)}
                          onOpen={(m, url) => openLightbox(m, url)}
                          onToggleRefile={(photoId) =>
                            setRefilingPhotoId(refilingPhotoId === photoId ? null : photoId)
                          }
                          onRotate={(photoId, degrees) => void rotatePhoto(photoId, degrees)}
                          onDelete={(photoId) => void deletePhoto(photoId)}
                          onRefile={(photoId, promptKey) => void refilePhoto(photoId, promptKey)}
                          {...(canEdit
                            ? {
                                onFixAssessment: (c: ClauseResponse) =>
                                  openEditor(clauseAnchorKey(c)),
                              }
                            : {})}
                        />
                      ) : (
                        <div className="clause-item" key={`${section}:${row.clauseId}`}>
                          <strong>{row.clauseId.replace(/([A-Za-z])\s+(?=\d)/g, '$1')}</strong>{' '}
                          <span className="muted">Unanswered</span>
                          <p>{row.question}</p>
                          {canEdit && (
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => openEditor(`${section}:${row.clauseId}`)}
                            >
                              Set assessment
                            </button>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                );
              })}
            </div>

            {jobId.startsWith('PVD') && (
              <div className="card">
                <h3>Non-checklist items</h3>
                {[...PV_NON_CHECKLIST_ITEMS].map(([id, item]) => (
                  <div key={id}>
                    <strong>
                      {id} — {item.description}
                    </strong>
                    <p>{submission.nonChecklistResponses?.[id] || 'Unanswered'}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {/* Photos the inspector imported but never filed. Surfaced above the
            main gallery because they are the actionable ones: until each is
            filed it satisfies no required shot and no clause. */}
        <UnfiledPhotosCard
          photos={photos.filter((p) => p.promptKey === UNASSIGNED_PROMPT_KEY)}
          submission={submission}
          canManage={canQa}
          photoActionBusyId={photoActionBusyId}
          refilingPhotoId={refilingPhotoId}
          onOpen={(m, url) => openLightbox(m, url)}
          onToggleRefile={(photoId) =>
            setRefilingPhotoId(refilingPhotoId === photoId ? null : photoId)
          }
          onRotate={(photoId, degrees) => void rotatePhoto(photoId, degrees)}
          onDelete={(photoId) => void deletePhoto(photoId)}
          onRefile={(photoId, promptKey) => void refilePhoto(photoId, promptKey)}
        />

        {/* Attach during QA. The reviewer could re-file an existing photo but
            never add one, so a shot the inspector missed meant a return visit.
            Files against the same prompt keys the field tool uses, so it lands
            in the portal payload like any other. */}
        <div className="card">
          <h3>Attach a photo</h3>
          <p className="muted">
            Saves immediately. Recorded as added during review; capture time is unverified.
          </p>
          <label>
            <span>Prompt</span>
            <select value={qaPhotoPrompt} onChange={(e) => setQaPhotoPrompt(e.target.value)}>
              <option value="">Select the shot this evidences…</option>
              <option value={GENERAL_RECOMMENDATIONS_PHOTO_PROMPT_KEY}>
                General recommendations · inspection / improvement photo
              </option>
              {getPromptsForJob(inspectionKindForJob(jobId)).map((prompt) => (
                <option key={prompt.promptKey} value={prompt.promptKey}>
                  {prompt.subject} · {prompt.shot}
                  {prompt.required ? ' (required)' : ''}
                </option>
              ))}
            </select>
          </label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={!qaPhotoPrompt || qaPhotoBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file || !qaPhotoPrompt) return;
              setQaPhotoBusy(true);
              setQaPhotoError(null);
              void uploadQaPhoto(jobId, {
                photoId: crypto.randomUUID(),
                promptKey: qaPhotoPrompt,
                file,
              })
                .then(() => {
                  setQaPhotoPrompt('');
                  load();
                })
                .catch((err: unknown) =>
                  setQaPhotoError(err instanceof Error ? err.message : 'Upload failed'),
                )
                .finally(() => setQaPhotoBusy(false));
            }}
          />
          {qaPhotoBusy && <p className="muted">Uploading…</p>}
          {qaPhotoError && <p className="error">{qaPhotoError}</p>}
        </div>

        {/* Photos (all, grouped by promptKey) ─────────────────────────── */}
        <div className="card" id="report-photos">
          <h3>Photos ({photos.length})</h3>
          <p className="muted">Rotating, re-filing and deleting photos saves immediately.</p>
          {photoActionError && <p className="error">{photoActionError}</p>}
          {photosByPromptKey.length === 0 ? (
            <p className="muted">No photos uploaded.</p>
          ) : (
            photosByPromptKey.map(([promptKey, list]) => (
              <div key={promptKey} className="photo-prompt-group">
                <h4>{photoPromptLabel(promptKey)}</h4>
                <div className="photo-grid photo-grid--editable">
                  {list.map((p, index) => (
                    <EditablePhotoTile
                      key={p.photoId}
                      manifest={p}
                      caption={`${photoPromptLabel(p.promptKey)} · ${index + 1}/${list.length}`}
                      canManage={canQa}
                      busy={photoActionBusyId === p.photoId}
                      refiling={refilingPhotoId === p.photoId}
                      onOpen={(url) => openLightbox(p, url)}
                      onToggleRefile={() =>
                        setRefilingPhotoId(refilingPhotoId === p.photoId ? null : p.photoId)
                      }
                      onRotate={(degrees) => void rotatePhoto(p.photoId, degrees)}
                      onDelete={() => void deletePhoto(p.photoId)}
                      filingPicker={
                        canQa && refilingPhotoId === p.photoId ? (
                          <FilingPicker
                            targets={filingTargetsFor(submission)}
                            busy={photoActionBusyId === p.photoId}
                            onCancel={() => setRefilingPhotoId(null)}
                            onPick={(nextKey) => void refilePhoto(p.photoId, nextKey)}
                          />
                        ) : null
                      }
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Audit trail ───────────────────────────────────────────────── */}
        {/* Two distinct trails, deliberately named apart: this one is the
            submission's own status transitions, the card below is the
            system-wide audit log for the job. */}
        <details className="card report-history">
          <summary>
            <span>Status history</span>
            <span className="report-history__meta">{audit?.length ?? 0} changes</span>
          </summary>
          <div className="report-history__body">
            {(!audit || audit.length === 0) && <p className="muted">No status changes yet.</p>}
            {audit && audit.length > 0 && (
              <ul className="audit-list">
                {audit.map((entry, i) => (
                  <li key={i} className="audit-entry">
                    <div className="audit-entry__when">
                      {formatDateTime(entry.at)} · {entry.actor}
                    </div>
                    <div className="audit-entry__transition">
                      <StatusPill status={entry.fromStatus} /> →{' '}
                      <StatusPill status={entry.toStatus} />
                    </div>
                    {entry.note && <div className="audit-entry__note">{entry.note}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>

        <details className="card report-history">
          <summary>
            <span>Audit trail</span>
            <span className="report-history__meta">Actions and changes</span>
          </summary>
          <div className="report-history__body">
            <AuditTimeline jobId={jobId} />
          </div>
        </details>
      </div>

      {canQa && sendBackOpen && (
        <SendBackModal
          busy={busy}
          allowInformation={canRequestInformation}
          allowRevisit={canRequireRevisit}
          onCancel={() => setSendBackOpen(false)}
          onSubmit={(status, note) => void doTransition(status, note)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          photo={lightbox}
          onClose={closeLightbox}
          saving={photoActionBusyId === lightbox.photoId}
          saveError={photoActionError}
          {...(canQa
            ? {
                onSaveRotation: (degrees: PhotoQuarterTurn) =>
                  rotatePhoto(lightbox.photoId, degrees),
              }
            : {})}
        />
      )}

      <p style={{ marginTop: 24, fontSize: 12 }}>
        <button type="button" onClick={() => void navigate(-1)}>
          ← Back
        </button>
      </p>
    </ReportSidebarLayout>
  );
}

function Row({ label, value, path }: { label: string; value: string; path?: string }) {
  const conflict = useHardwareConflict(path);
  return (
    <div
      className="card__row"
      style={
        conflict ? { background: '#fffbeb', outline: '1px solid #d69e2e', padding: 8 } : undefined
      }
    >
      <dt>{label}</dt>
      <dd>
        {value}
        {path && <ConflictHint path={path} />}
      </dd>
    </div>
  );
}

function formatJobAddress(address: {
  line1: string;
  suburb: string;
  state: string;
  postcode: string;
}): string {
  return [address.line1, address.suburb, [address.state, address.postcode].join(' ').trim()]
    .filter(Boolean)
    .join(', ');
}

function tendererFromClient(clientId: string): FieldReport['cover']['tenderer'] | undefined {
  const normalised = clientId.toLowerCase();
  if (normalised.includes('kerfoot')) return 'Kerfoot';
  if (normalised.includes('itp')) return 'ITP';
  if (normalised.includes('smart')) return 'Smart Energy';
  if (normalised.includes('mea')) return 'MEA';
  if (normalised.includes('gses')) return 'GSES';
  if (normalised.includes('techsafe') || normalised.includes('tech-safe')) return 'Tech Safe';
  return undefined;
}

function ClauseItem({
  clause,
  question,
  photoById,
  canManage,
  photoActionBusyId,
  refilingPhotoId,
  filingTargets,
  onOpen,
  onToggleRefile,
  onRotate,
  onDelete,
  onRefile,
  onFixAssessment,
}: {
  clause: ClauseResponse;
  question?: string | undefined;
  photoById: Map<string, StoredPhotoManifest>;
  canManage: boolean;
  photoActionBusyId: string | null;
  refilingPhotoId: string | null;
  filingTargets: FilingTargetGroup[];
  onOpen: (manifest: StoredPhotoManifest, blobUrl: string) => void;
  onToggleRefile: (photoId: string) => void;
  onRotate: (photoId: string, degrees: PhotoQuarterTurn) => void;
  onDelete: (photoId: string) => void;
  onRefile: (photoId: string, promptKey: string) => void;
  /** Opens the editor focused on this clause. Absent for read-only viewers. */
  onFixAssessment?: (clause: ClauseResponse) => void;
}) {
  const outcomeClass =
    clause.outcome === 'Compliant'
      ? 'clause-count--c'
      : clause.outcome === 'Non-Compliant'
        ? 'clause-count--nc'
        : 'clause-count--na';
  const customResponse = isCustomResponse(clause.section, clause.clauseId, clause.recommendation);
  const missingAssessment = clause.outcome === 'Non-Compliant' && !assessmentLabel(clause);
  return (
    <div className="clause-item">
      <div className="clause-item__head">
        <span className="clause-item__id">
          {clause.clauseId.replace(/([A-Za-z])\s+(?=\d)/g, '$1')}
        </span>
        <span className={`clause-count ${outcomeClass}`}>{clause.outcome}</span>
        {assessmentLabel(clause) && (
          <span className="clause-count clause-count--nc">
            Assessment: {assessmentLabel(clause)}
          </span>
        )}
        {missingAssessment &&
          (onFixAssessment ? (
            <button
              type="button"
              className="link-button"
              onClick={() => onFixAssessment(clause)}
              title="Open the editor on this clause to pick the portal assessment and wording."
            >
              Set assessment
            </button>
          ) : (
            <span className="muted">No assessment picked</span>
          ))}
      </div>
      {question && <p>{question}</p>}
      <dl className="clause-item__body">
        {clause.recommendation && (
          <div className="clause-item__field">
            <dt>Recommendation</dt>
            <dd className={customResponse ? 'custom-response' : undefined}>
              {clause.recommendation}
              {customResponse && <small className="custom-response__note">Custom Response</small>}
            </dd>
          </div>
        )}
        {clause.inspectorNote && (
          <div className="clause-item__field">
            <dt>Inspector note</dt>
            <dd>{clause.inspectorNote}</dd>
          </div>
        )}
      </dl>
      {clause.photoIds.length > 0 && (
        <div className="photo-grid photo-grid--editable" style={{ marginTop: 8 }}>
          {clause.photoIds.map((photoId) => {
            const manifest = photoById.get(photoId);
            if (!manifest) {
              return (
                <div key={photoId} className="photo-tile">
                  <div className="photo-thumb">
                    <div className="photo-thumb__loading">missing</div>
                    <div className="photo-thumb__caption">{photoId.slice(0, 8)}…</div>
                  </div>
                </div>
              );
            }
            return (
              <EditablePhotoTile
                key={photoId}
                manifest={manifest}
                caption={photoPromptLabel(manifest.promptKey)}
                canManage={canManage}
                busy={photoActionBusyId === photoId}
                refiling={refilingPhotoId === photoId}
                onOpen={(url) => onOpen(manifest, url)}
                onToggleRefile={() => onToggleRefile(photoId)}
                onRotate={(degrees) => onRotate(photoId, degrees)}
                onDelete={() => onDelete(photoId)}
                filingPicker={
                  canManage && refilingPhotoId === photoId ? (
                    <FilingPicker
                      targets={filingTargets}
                      busy={photoActionBusyId === photoId}
                      onCancel={() => onToggleRefile(photoId)}
                      onPick={(promptKey) => onRefile(photoId, promptKey)}
                    />
                  ) : null
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function assessmentLabel(clause: ClauseResponse): string | undefined {
  const value = assessmentValueForClauseResponse(clause);
  return (
    findAssessmentOption(clause.section, clause.clauseId, value)?.label ??
    assessmentLabelForValue(value) ??
    clause.severity
  );
}

function SendBackModal({
  busy,
  allowInformation,
  allowRevisit,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  allowInformation: boolean;
  allowRevisit: boolean;
  onCancel: () => void;
  onSubmit: (status: 'information-required' | 'revisit-required', note: string) => void;
}) {
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'information-required' | 'revisit-required'>(
    allowInformation ? 'information-required' : 'revisit-required',
  );
  const trimmed = note.trim();
  return (
    <div className="modal" onClick={onCancel}>
      <div className="modal__card" onClick={(e) => e.stopPropagation()}>
        <h3>Return to inspector</h3>
        <p className="subtitle">
          Choose whether this can be resolved remotely or requires another site visit.
        </p>
        <fieldset>
          <legend>Required action</legend>
          {allowInformation && (
            <label>
              <input
                type="radio"
                name="return-kind"
                checked={status === 'information-required'}
                onChange={() => setStatus('information-required')}
              />{' '}
              Additional information required
            </label>
          )}
          {allowRevisit && (
            <label>
              <input
                type="radio"
                name="return-kind"
                checked={status === 'revisit-required'}
                onChange={() => setStatus('revisit-required')}
              />{' '}
              Return to site required
            </label>
          )}
        </fieldset>
        <label>
          <span>Note</span>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Re-shoot the switchboard label photo; current shot is out of focus."
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="danger"
            disabled={busy || !trimmed}
            onClick={() => onSubmit(status, trimmed)}
          >
            {busy
              ? 'Sending…'
              : status === 'revisit-required'
                ? 'Require site revisit'
                : 'Request information'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Photos the inspector imported but never filed.
 *
 * The dashboard half of the two-stage workflow: an inspector can leave site
 * having captured everything and filed nothing, and whoever writes the report
 * finishes the filing here rather than sending the job back for it.
 *
 * Deliberately its own card above the main gallery. An unfiled photo satisfies
 * no required shot and no clause, so it is the only part of the gallery that
 * is actionable — burying it among filed photos would make the QA panel's
 * complaints look unexplainable.
 */
function UnfiledPhotosCard({
  photos,
  submission,
  canManage,
  photoActionBusyId,
  refilingPhotoId,
  onOpen,
  onToggleRefile,
  onRotate,
  onDelete,
  onRefile,
}: {
  photos: StoredPhotoManifest[];
  submission: FieldReport;
  canManage: boolean;
  photoActionBusyId: string | null;
  refilingPhotoId: string | null;
  onOpen: (manifest: StoredPhotoManifest, blobUrl: string) => void;
  onToggleRefile: (photoId: string) => void;
  onRotate: (photoId: string, degrees: PhotoQuarterTurn) => void;
  onDelete: (photoId: string) => void;
  onRefile: (photoId: string, promptKey: string) => void;
}) {
  if (photos.length === 0) return null;

  const targets = filingTargetsFor(submission);

  return (
    <div className="card">
      <h3>Not yet filed ({photos.length})</h3>
      <p className="muted">
        Imported by the inspector but not assigned to a shot or clause. Until each is filed it
        counts towards nothing in QA. Rotate, re-file and delete save immediately.
      </p>
      <div className="photo-grid photo-grid--editable">
        {photos.map((p) => (
          <EditablePhotoTile
            key={p.photoId}
            manifest={p}
            caption={p.filename}
            canManage={canManage}
            busy={photoActionBusyId === p.photoId}
            refiling={refilingPhotoId === p.photoId}
            onOpen={(url) => onOpen(p, url)}
            onToggleRefile={() => onToggleRefile(p.photoId)}
            onRotate={(degrees) => onRotate(p.photoId, degrees)}
            onDelete={() => onDelete(p.photoId)}
            filingPicker={
              canManage && refilingPhotoId === p.photoId ? (
                <FilingPicker
                  targets={targets}
                  busy={photoActionBusyId === p.photoId}
                  onCancel={() => onToggleRefile(p.photoId)}
                  onPick={(promptKey) => onRefile(p.photoId, promptKey)}
                />
              ) : null
            }
          />
        ))}
      </div>
    </div>
  );
}
